import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, FileText, X } from "lucide-react";
import { PROFILES } from "@/constants/assets";
import { DIVIDER_BARS } from "@/data/dividerBars";

const DIVIDER_BAR_PHOTOS = [
  {
    src: "/technical/divider-bar/divider-1.webp",
    name: "Flat T",
    alt: "Flat T divider bar — horizontal astragal across double-glazed pane",
  },
  {
    src: "/technical/divider-bar/divider-2.webp",
    name: "Compact Pyramid",
    alt: "Compact Pyramid divider bar — astragal over textured obscure glass",
  },
  {
    src: "/technical/divider-bar/divider-3.webp",
    name: "Slim Ridge",
    alt: "Slim Ridge divider bar — steel astragal on FerroFinestra frame",
  },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.7, ease: [0.19, 1, 0.22, 1] },
  }),
};

export default function Technical() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const lightboxOpen = lightboxIndex !== null;

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight")
        setLightboxIndex((i) => (i + 1) % DIVIDER_BAR_PHOTOS.length);
      if (e.key === "ArrowLeft")
        setLightboxIndex(
          (i) => (i - 1 + DIVIDER_BAR_PHOTOS.length) % DIVIDER_BAR_PHOTOS.length,
        );
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen]);

  return (
    <div
      data-testid="technical-page"
      className="relative min-h-screen bg-[#0a0a0c] pt-32 lg:pt-40"
    >
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 pb-20 lg:px-12">
        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          className="font-tech text-[11px] uppercase tracking-[0.4em] text-[#c6a87c]"
        >
          Technical Library
        </motion.p>
        <motion.h1
          initial="hidden"
          animate="show"
          variants={fade}
          custom={1}
          className="mt-6 font-serif-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl"
          data-testid="technical-heading"
        >
          Specifications & downloads.
        </motion.h1>
        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          custom={2}
          className="mt-8 max-w-2xl font-body text-base leading-relaxed text-white/60 sm:text-lg"
        >
          Downloadable spec sheets, profile drawings, glazing options and hardware libraries — the
          full FerroFinestra reference. More coming soon.
        </motion.p>
      </section>

      <div className="mx-auto max-w-[1400px] border-t border-white/10" />

      {/* Profile Specifications */}
      <section
        id="profile-specifications"
        data-testid="profile-specifications"
        className="mx-auto max-w-[1400px] scroll-mt-32 px-6 py-20 lg:px-12"
      >
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
              — Section 01
            </p>
            <h2 className="mt-4 font-serif-display text-3xl leading-tight text-white lg:text-4xl">
              Profile Specifications
            </h2>
          </div>
          <p className="max-w-md font-body text-sm leading-relaxed text-white/50">
            Every FerroFinestra profile, with dimensions, sight lines, glazing options and mounting
            detail. Tap to download the PDF spec sheet.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PROFILES.map((p, i) => (
            <motion.article
              key={p.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.05 + i * 0.05, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              data-testid={`tech-profile-${p.code.toLowerCase()}`}
              className="group flex flex-col border border-white/10 bg-white/[0.02] p-8 transition-colors hover:border-[#c6a87c]/40"
            >
              {/* Image */}
              <div className="flex h-[220px] w-full items-center justify-center overflow-hidden bg-white lg:h-[240px]">
                <img
                  src={p.image}
                  alt={p.name}
                  draggable={false}
                  className="max-h-full max-w-full select-none object-contain transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  style={{
                    filter:
                      "drop-shadow(0 20px 24px rgba(0,0,0,0.10)) drop-shadow(0 6px 8px rgba(0,0,0,0.06))",
                  }}
                />
              </div>

              {/* Meta */}
              <div className="mt-6 flex items-start justify-between gap-4">
                <div>
                  <p className="font-tech text-[10px] uppercase tracking-[0.32em] text-white/40">
                    {p.code}
                  </p>
                  <h3 className="mt-2 font-serif-display text-2xl leading-tight text-white">
                    {p.name}
                  </h3>
                  <p className="font-tech mt-1 text-[10px] uppercase tracking-[0.24em] text-[#c6a87c]">
                    {p.tagline}
                  </p>
                </div>
                <span className="font-tech text-[10px] text-white/25">0{i + 1}</span>
              </div>

              <p className="mt-4 flex-1 font-body text-[13px] leading-[1.7] text-white/55">
                {p.description}
              </p>

              {/* Spec sheet action */}
              <div className="mt-6 border-t border-white/10 pt-5">
                {p.specSheet ? (
                  <a
                    href={p.specSheet}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`tech-spec-${p.code.toLowerCase()}`}
                    className="inline-flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.24em] text-[#c6a87c] transition-colors hover:text-[#e2c69a]"
                  >
                    <Download size={12} strokeWidth={1.6} />
                    <span>Download Spec Sheet · PDF</span>
                  </a>
                ) : (
                  <span
                    data-testid={`tech-spec-soon-${p.code.toLowerCase()}`}
                    className="inline-flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.24em] text-white/30"
                  >
                    <FileText size={12} strokeWidth={1.6} />
                    <span>Spec Sheet · Coming Soon</span>
                  </span>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Glass Samples */}
      <section
        id="glass-samples"
        data-testid="technical-glass-samples"
        className="mx-auto max-w-[1400px] scroll-mt-32 border-t border-white/10 px-6 py-20 lg:px-12"
      >
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
              — Section 02
            </p>
            <h2 className="mt-4 font-serif-display text-3xl leading-tight text-white lg:text-4xl">
              Glazing Types
            </h2>
          </div>
          <p className="max-w-md font-body text-sm leading-relaxed text-white/50">
            Standard glazing options across the Eurosteel range — from crystal float to
            obscured textured patterns. Photographed against a neutral backdrop so you
            can compare privacy and light transmission at a glance.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-6">
          {[
            {
              name: "Rain",
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/hbh1f1pd_IMG_4944.webp",
              blurb: "Vertical streaked texture — high privacy, soft daylight.",
              rotate: true,
            },
            {
              name: "Flemish",
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/28crzk2q_IMG_4947.webp",
              blurb: "Gentle organic ripple — light obscure, timeless.",
            },
            {
              name: "Fluted",
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/ogzzpzua_IMG_4941.webp",
              blurb: "Ribbed vertical flutes — modern privacy with directional light.",
            },
            {
              name: "Clear",
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/nsvh5qvl_IMG_4948.webp",
              blurb: "Standard float glass — maximum transparency.",
            },
            {
              name: "Tea",
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/zjliadjf_IMG_4949.webp",
              blurb: "Warm bronze body tint — solar control with view retained.",
            },
            {
              name: "Watercube",
              src: "https://customer-assets-jt897jd0.emergentagent.net/job_ad6d47a9-56c8-4b87-bcde-9129ed0761e2/artifacts/sz52h2sd_IMG_4950.webp",
              blurb: "Faceted honeycomb texture — decorative privacy.",
            },
          ].map((g, i) => (
            <motion.figure
              key={g.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.06, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              data-testid={`glass-sample-${g.name.toLowerCase()}`}
              className="group flex flex-col"
            >
              <div className="relative aspect-square w-full overflow-hidden bg-neutral-800">
                <img
                  src={g.src}
                  alt={`${g.name} glass sample`}
                  loading="lazy"
                  draggable={false}
                  className={`absolute inset-0 h-full w-full select-none object-cover object-center transition-transform duration-[900ms] ease-out group-hover:scale-[1.04] ${
                    g.rotate ? "rotate-90 scale-125 group-hover:scale-[1.30]" : ""
                  }`}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10"
                />
              </div>
              <figcaption className="mt-4">
                <p className="font-tech text-[10px] font-bold uppercase tracking-[0.3em] text-[#c6a87c]">
                  {g.name}
                </p>
                <p className="mt-2 font-body text-[12px] leading-[1.6] text-white/55">
                  {g.blurb}
                </p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Certification table — AS/NZS 2208: 2023 */}
        <div className="mt-20 border-t border-white/10 pt-14">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
                Compliance
              </p>
              <h3 className="mt-3 font-serif-display text-2xl leading-tight text-white lg:text-3xl">
                Glass certified to AS/NZS 2208: 2023
              </h3>
            </div>
            <p className="max-w-md font-body text-sm leading-relaxed text-white/50">
              Every pane specified through Eurosteel meets the Safety Glazing Materials
              in Buildings standard. Full range of thicknesses, coatings and interlayers
              available on request.
            </p>
          </div>

          <div
            data-testid="glass-cert-table"
            className="mt-10 overflow-x-auto border border-white/10 bg-white/[0.02]"
          >
            <table className="w-full min-w-[880px] border-collapse text-left">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.03]">
                  {[
                    "Types of product",
                    "Glass thickness (mm)",
                    "Glass colours",
                    "Grade",
                    "Coatings",
                    "Interlayer type",
                    "Interlayer thickness (mm)",
                  ].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="whitespace-nowrap px-5 py-4 font-tech text-[10px] font-bold uppercase tracking-[0.24em] text-[#c6a87c]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    type: "Toughened",
                    thickness: "4 – 15",
                    colours: ["Clear", "Ultra Clear", "Grey", "Bronze"],
                    grade: "A",
                    coatings: "—",
                    interType: "—",
                    interThick: "—",
                  },
                  {
                    type: "Toughened",
                    thickness: "5 – 12",
                    colours: ["Clear", "Ultra Clear"],
                    grade: "A",
                    coatings: "Low-E",
                    interType: "—",
                    interThick: "—",
                  },
                  {
                    type: "Toughened Laminated",
                    thickness: "8 – 24",
                    colours: ["Clear", "Ultra Clear", "Grey", "Bronze"],
                    grade: "A",
                    coatings: "—",
                    interType: "PVB",
                    interThick: "0.76 – 3.04",
                  },
                  {
                    type: "Toughened Laminated",
                    thickness: "10 – 30",
                    colours: ["Clear", "Ultra Clear", "Grey", "Bronze"],
                    grade: "A",
                    coatings: "—",
                    interType: "SGP",
                    interThick: "0.76 – 3.04",
                  },
                  {
                    type: "Heat Strengthened Laminated",
                    thickness: "8 – 24",
                    colours: ["Clear", "Ultra Clear", "Grey", "Bronze"],
                    grade: "A",
                    coatings: "—",
                    interType: "PVB",
                    interThick: "0.76 – 3.04",
                  },
                  {
                    type: "Heat Strengthened Laminated",
                    thickness: "10 – 30",
                    colours: ["Clear", "Ultra Clear", "Grey", "Bronze"],
                    grade: "A",
                    coatings: "—",
                    interType: "SGP",
                    interThick: "0.76 – 3.04",
                  },
                  {
                    type: "Laminated",
                    thickness: "8 – 24",
                    colours: ["Clear", "Ultra Clear", "Grey", "Bronze"],
                    grade: "A",
                    coatings: "—",
                    interType: "PVB",
                    interThick: "0.38 – 1.52",
                  },
                ].map((row, i) => (
                  <tr
                    key={`${row.type}-${row.thickness}-${row.interType}`}
                    data-testid={`glass-cert-row-${i}`}
                    className="border-b border-white/5 transition-colors hover:bg-white/[0.03] last:border-b-0"
                  >
                    <td className="px-5 py-4 align-top font-body text-[13px] font-semibold text-white">
                      {row.type}
                    </td>
                    <td className="px-5 py-4 align-top font-tech text-[12px] text-white/80">
                      {row.thickness}
                    </td>
                    <td className="px-5 py-4 align-top font-body text-[13px] leading-[1.7] text-white/70">
                      {row.colours.join(", ")}
                    </td>
                    <td className="px-5 py-4 align-top font-tech text-[12px] uppercase tracking-[0.14em] text-[#c6a87c]">
                      {row.grade}
                    </td>
                    <td className="px-5 py-4 align-top font-tech text-[12px] text-white/70">
                      {row.coatings}
                    </td>
                    <td className="px-5 py-4 align-top font-tech text-[12px] text-white/70">
                      {row.interType}
                    </td>
                    <td className="px-5 py-4 align-top font-tech text-[12px] text-white/70">
                      {row.interThick}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-4 font-tech text-[10px] uppercase tracking-[0.24em] text-white/40">
            Grade A denotes the highest safety classification for architectural glazing under AS/NZS 2208: 2023.
          </p>
        </div>
      </section>

      {/* Divider Bar Configuration */}
      <section
        id="divider-bars"
        data-testid="technical-divider-bars"
        className="mx-auto max-w-[1400px] scroll-mt-32 border-t border-white/10 px-6 py-20 lg:px-12"
      >
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
              — Section 03
            </p>
            <h2 className="mt-3 font-serif-display text-3xl leading-[1.05] text-white sm:text-4xl lg:text-5xl">
              Divider Bar Configuration
            </h2>
          </div>
          <p className="max-w-xl font-body text-[14px] leading-[1.75] text-white/60">
            Decorative astragals that clip onto the glass to divide it into lites. Every profile
            is machined from solid steel or aluminium and colour-matched to the frame.
          </p>
        </div>

        <div
          data-testid="divider-bar-grid"
          className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        >
          {DIVIDER_BARS.map(({ id, name, spec, image }, i) => (
            <motion.figure
              key={id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fade}
              custom={i}
              data-testid={`divider-bar-${id}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] transition-all duration-500 hover:border-[#c6a87c]/60 hover:bg-white/[0.07] hover:shadow-[0_20px_60px_-20px_rgba(198,168,124,0.35)]"
            >
              <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_50%_60%,rgba(198,168,124,0.09),rgba(0,0,0,0)_65%)]">
                <img
                  src={image}
                  alt={`${name} — ${spec}`}
                  loading="lazy"
                  className="max-h-[75%] max-w-[80%] object-contain"
                  style={{
                    filter: "invert(1) contrast(1.05) brightness(0.95)",
                    mixBlendMode: "screen",
                  }}
                />
              </div>
              <figcaption className="border-t border-white/5 px-4 py-3">
                <p className="font-tech text-[9px] uppercase tracking-[0.25em] text-[#c6a87c]/70">
                  {id}
                </p>
                <p className="mt-1 font-serif-display text-[15px] font-bold text-white">
                  {name}
                </p>
                <p className="mt-1 font-tech text-[10px] uppercase tracking-[0.18em] text-white/45">
                  {spec}
                </p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <p className="mt-6 font-tech text-[10px] uppercase tracking-[0.24em] text-white/40">
          Non-standard profiles machined to order on request.
        </p>

        {/* Real-life divider bar photos — click to enlarge */}
        <div className="mt-14 border-t border-white/10 pt-10">
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
                — On the glass
              </p>
              <h3 className="mt-2 font-serif-display text-xl text-white sm:text-2xl">
                Divider bars in situ.
              </h3>
            </div>
            <p className="max-w-md font-tech text-[10px] uppercase tracking-[0.22em] text-white/40">
              Tap any image to enlarge
            </p>
          </div>

          <div
            data-testid="divider-bar-photos-grid"
            className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3"
          >
            {DIVIDER_BAR_PHOTOS.map((photo, i) => (
              <motion.button
                key={photo.src}
                type="button"
                onClick={() => setLightboxIndex(i)}
                data-testid={`divider-bar-photo-${i}`}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-40px" }}
                variants={fade}
                custom={i}
                className="group relative block aspect-[16/10] w-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all duration-500 hover:border-[#c6a87c]/60 hover:shadow-[0_20px_60px_-20px_rgba(198,168,124,0.35)]"
                aria-label={`Enlarge divider bar photo ${i + 1}`}
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  draggable={false}
                  className="h-full w-full select-none object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.04]"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 via-black/25 to-transparent px-3 py-2.5 font-tech text-[10px] uppercase tracking-[0.24em] text-white"
                >
                  <span data-testid={`divider-bar-photo-name-${i}`}>{photo.name}</span>
                  <span className="text-[9px] text-white/60 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    View →
                  </span>
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxOpen && (
            <motion.div
              data-testid="divider-bar-lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
              onClick={() => setLightboxIndex(null)}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(null);
                }}
                data-testid="divider-bar-lightbox-close"
                className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white/80 transition-all duration-300 hover:border-[#c6a87c] hover:bg-[#c6a87c]/10 hover:text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(
                    (i) =>
                      (i - 1 + DIVIDER_BAR_PHOTOS.length) %
                      DIVIDER_BAR_PHOTOS.length,
                  );
                }}
                data-testid="divider-bar-lightbox-prev"
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/5 px-4 py-2 font-tech text-[10px] uppercase tracking-[0.28em] text-white/80 transition-all duration-300 hover:border-[#c6a87c] hover:text-white md:left-8"
                aria-label="Previous"
              >
                ←
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(
                    (i) => (i + 1) % DIVIDER_BAR_PHOTOS.length,
                  );
                }}
                data-testid="divider-bar-lightbox-next"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-white/5 px-4 py-2 font-tech text-[10px] uppercase tracking-[0.28em] text-white/80 transition-all duration-300 hover:border-[#c6a87c] hover:text-white md:right-8"
                aria-label="Next"
              >
                →
              </button>

              <motion.img
                key={DIVIDER_BAR_PHOTOS[lightboxIndex].src}
                src={DIVIDER_BAR_PHOTOS[lightboxIndex].src}
                alt={DIVIDER_BAR_PHOTOS[lightboxIndex].alt}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="max-h-[85vh] max-w-[92vw] select-none rounded-lg object-contain shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]"
                draggable={false}
              />

              <p
                data-testid="divider-bar-lightbox-name"
                className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-3 font-tech text-[10px] uppercase tracking-[0.32em] text-white/60"
              >
                <span className="text-white/85">
                  {DIVIDER_BAR_PHOTOS[lightboxIndex].name}
                </span>
                <span className="h-[1px] w-8 bg-white/25" />
                <span>
                  {String(lightboxIndex + 1).padStart(2, "0")} /{" "}
                  {String(DIVIDER_BAR_PHOTOS.length).padStart(2, "0")}
                </span>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Mounting Types */}
      <section
        id="mounting-types"
        data-testid="technical-mounting-types"
        className="mx-auto max-w-[1400px] scroll-mt-32 border-t border-white/10 px-6 py-20 lg:px-12"
      >
        <div>
          <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
            — Section 04
          </p>
          <h2 className="mt-3 font-serif-display text-3xl leading-[1.05] text-white sm:text-4xl lg:text-5xl">
            Mounting Types
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
          data-testid="mounting-types-diagram"
          className="mt-12 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-10"
        >
          <img
            src="/mounting-types.png"
            alt="Mounting types — Nailing fins, Bolts thru jamb, Mounting tabs, Hurricane tabs"
            loading="lazy"
            draggable={false}
            className="w-full select-none object-contain"
            style={{
              filter: "invert(1) contrast(1.05) brightness(0.95)",
              mixBlendMode: "screen",
            }}
          />
        </motion.div>
      </section>

      {/* Downloadable Resources */}
      <section
        id="resources"
        data-testid="technical-resources"
        className="mx-auto max-w-[1400px] scroll-mt-32 border-t border-white/10 px-6 py-20 lg:px-12"
      >
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]">
              — Section 05
            </p>
            <h2 className="mt-4 font-serif-display text-3xl leading-tight text-white lg:text-4xl">
              Resources & Guides
            </h2>
          </div>
          <p className="max-w-md font-body text-sm leading-relaxed text-white/50">
            Reference documents for architects, builders and homeowners. More coming soon.
          </p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[
            {
              id: "measuring-guide",
              label: "Measuring Guide",
              detail: "Hinged Doors · PDF",
              copy: "Step-by-step guide to measuring finished openings for Eurosteel hinged doors — width, head height, sill and tolerances.",
              href: "/specs/Eurosteel-Measuring-Guide-Hinged-Doors.pdf",
            },
            {
              id: "w20-spec",
              label: "W20 Profile Spec",
              detail: "FerroFinestra · PDF",
              copy: "Full profile drawings and dimensions for the slim single-glazed W20 steel system.",
              href: "/specs/FF-W20.pdf",
            },
            {
              id: "w40-spec",
              label: "W40 Profile Spec",
              detail: "FerroFinestra · PDF",
              copy: "Full profile drawings and dimensions for the 40mm double-glazed W40 external steel system.",
              href: "/specs/FF-W40.pdf",
            },
            {
              id: "iver-hardware",
              label: "Iver Hardware Lookbook",
              detail: "Hardware · PDF",
              copy: "The full Iver 2024 hardware range — door pulls, levers, handles and finishes available across the Eurosteel product line.",
              href: "/specs/iver-lookbook-2024.pdf",
            },
          ].map((r, i) => (
            <motion.a
              key={r.id}
              href={r.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`resource-${r.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.05, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
              className="group flex flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-7 transition-all duration-500 hover:border-[#c6a87c]/50 hover:bg-white/[0.04] hover:shadow-[0_20px_60px_-30px_rgba(198,168,124,0.35)]"
            >
              <div>
                <div className="flex items-center justify-between">
                  <p className="font-tech text-[10px] uppercase tracking-[0.32em] text-[#c6a87c]/70">
                    {r.detail}
                  </p>
                  <span className="font-tech text-[10px] text-white/25">0{i + 1}</span>
                </div>
                <h3 className="mt-4 font-serif-display text-2xl leading-tight text-white">
                  {r.label}
                </h3>
                <p className="mt-3 font-body text-[13px] leading-[1.7] text-white/55">
                  {r.copy}
                </p>
              </div>
              <div className="mt-6 border-t border-white/10 pt-5">
                <span className="inline-flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.24em] text-[#c6a87c] transition-colors group-hover:text-[#e2c69a]">
                  <Download size={12} strokeWidth={1.6} />
                  <span>Download PDF</span>
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="mx-auto max-w-[1400px] border-t border-white/10 px-6 py-20 lg:px-12">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
              Need something specific?
            </p>
            <h3 className="mt-4 max-w-xl font-serif-display text-2xl leading-tight text-white lg:text-3xl">
              CAD blocks, BIM families and system drawings are available on request.
            </h3>
          </div>
          <a
            href="mailto:isaiah@euro-steel.co.nz?subject=Technical%20Request"
            data-testid="technical-request-btn"
            className="whitespace-nowrap rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[11px] uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
          >
            Request Files
          </a>
        </div>
      </section>
    </div>
  );
}
